import { Link } from "react-router-dom";
import { ArrowLeft, Users, Tv, Bug, Trash2, Target } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSupabase } from "@/hooks/useSupabase";
import BackupRestore from "@/components/BackupRestore";

export default function Admin() {
  const { store } = useSupabase();
  const [isClearing, setIsClearing] = useState(false);
  const [message, setMessage] = useState("");

  const handleClearAllAttempts = async () => {
    setIsClearing(true);
    setMessage("");

    try {
      const removedCount = await store.clearAllAttempts();
      setMessage(`✅ ${removedCount} tentativas foram removidas com sucesso!`);
    } catch (error) {
      console.error("Erro ao limpar tentativas:", error);
      setMessage("❌ Erro ao limpar tentativas. Tente novamente.");
    } finally {
      setIsClearing(false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-primary text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div className="flex items-center space-x-3">
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2F525330fe20684a269584d84d399c39f6%2Fe804dffb024540e5b053023d69334fd2?format=webp&width=800"
                alt="Boulder Academia Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-3xl font-bold">Administração</h1>
                <p className="text-blue-100">Boulder Academia</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Área Administrativa
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Gerencie atletas, visualize rankings e faça backup dos dados do
            festival.
          </p>
        </div>

        {/* Navigation Cards */}
        <div className="flex flex-col gap-6 max-w-5xl mx-auto mb-12">
          {/* Cadastro de Atletas */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-lg">Cadastro de Atletas</CardTitle>
              <CardDescription>
                Cadastrar e gerenciar atletas do festival
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/atletas">
                <Button className="w-full">Acessar</Button>
              </Link>
            </CardContent>
          </Card>

          {/* Ranking Telão */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Tv className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-lg">Ranking Telão</CardTitle>
              <CardDescription>Visualização para projeção</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/ranking">
                <Button className="w-full">Acessar</Button>
              </Link>
            </CardContent>
          </Card>

          {/* Route Performance */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-lg">Performance das Vias</CardTitle>
              <CardDescription>
                Análise de desempenho para Route Setters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/route-performance">
                <Button className="w-full">Acessar</Button>
              </Link>
            </CardContent>
          </Card>
          {/* Debug */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group border-orange-200">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <Bug className="w-8 h-8 text-orange-600" />
              </div>
              <CardTitle className="text-lg text-orange-800">
                Debug & Diagnóstico
              </CardTitle>
              <CardDescription>
                Testar conectividade e diagnosticar problemas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/debug">
                <Button
                  variant="outline"
                  className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  Acessar Debug
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Clear All Attempts */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group border-red-200">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-red-200 transition-colors">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-lg text-red-800">
                Limpar Tentativas
              </CardTitle>
              <CardDescription>
                Remover todas as tentativas de todos os atletas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="w-full"
                    disabled={isClearing}
                  >
                    {isClearing ? (
                      <>
                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Limpando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Limpar Todas as Tentativas
                      </>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      ⚠️ ATENÇÃO - Ação Irreversível
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div>
                        <p className="mb-4">
                          <strong>
                            Esta ação irá remover TODAS as tentativas de TODOS
                            os atletas!
                          </strong>
                        </p>
                        <p className="mb-2">Isso significa que:</p>
                        <ul className="list-disc ml-6 mb-4">
                          <li>
                            Todos os registros de tentativas serão perdidos
                          </li>
                          <li>Todas as pontuações serão zeradas</li>
                          <li>O ranking será resetado</li>
                          <li>Esta ação NÃO PODE ser desfeita</li>
                        </ul>
                        <p>
                          <strong>
                            Tem certeza absoluta que deseja continuar?
                          </strong>
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleClearAllAttempts}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Sim, Limpar TODAS as Tentativas
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {message && (
                <div className="mt-3 p-2 text-sm text-center rounded-md bg-gray-100">
                  {message}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Backup Section */}
        <div className="max-w-2xl mx-auto">
          <BackupRestore />
        </div>

        {/* Additional Info */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8 max-w-4xl mx-auto">
          <h3 className="text-xl font-semibold mb-4 text-center">
            Informações do Sistema
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <h4 className="font-semibold text-primary mb-2">Horários</h4>
              <p className="text-gray-600">Bateria 1: 11:00</p>
              <p className="text-gray-600">Bateria 2: 14:30</p>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-2">Vias</h4>
              <p className="text-gray-600">40 vias numeradas</p>
              <p className="text-gray-600">Dificuldade crescente</p>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-2">Pontuação</h4>
              <p className="text-gray-600">Flash: 1ª tentativa</p>
              <p className="text-gray-600">2ª+: Demais tentativas</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; 2024 Festival de Escalada - Boulder Academia</p>
        </div>
      </footer>
    </div>
  );
}
